import { useCallback, useContext, useEffect, useState } from "react";
import {
  Grid,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Control, Controller, FieldValues, useFormContext } from "react-hook-form";
import useDeleteProposalPlantProduct from "hooks/proposals/useDeleteProposalPlantProduct";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import StageProductContext from "./forms/StageProductContext";
import useProposalProductDetails from "hooks/products/useProposalProductDetails";
import pluralMeasurementMaterial from "constants/pluralMeasurementMaterial";
import ProposalContext from "./providers/ProposalContextProvider";
import currencyEndAdornment from "constants/currencyEndAdornment";
import PlantProductTruckingTable from "./forms/subforms/PlantProductTruckingTable";
import timeEndAdornment from "constants/timeEndAdornment";
import minuteEndAdornment from "constants/minuteEndAdornment";
import { convertHrsToMins } from "DDD/action-objects/StageProductUpdate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import MaterialsNoPlantSection from "./MaterialsNoPlantSection";
import useUpdateProposalPlantProduct from "hooks/proposals/useUpdateProposalPlantProduct";
import PlantOptions from "modules/plants/PlantOptions";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import { Pagination } from "hooks/usePaginatedResources";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { mapPlantProductTypeOptions } from "constants/PlantProductTypes";
import { ProposalPlantProduct, ProposalProduct } from "generated/graphql";
import { sentenceCase } from "change-case";
import Decimal from "decimal.js";

type PlantProductSelectionFieldsProps = {
  proposalPlantProduct: ProposalPlantProduct;
  index: number;
  plantOptions: SelectOptions;
  plantProductOptions: Record<string, SelectOptions>;
  plantsLoading: boolean;
  search: (v: string) => void;
  pagination: Pagination;
  inViewMode: boolean;
  control: Control<FieldValues>;
  updateProposalPlantProduct: (params: {
    variables: {
      plantProductId: string;
      proposalProductId: string;
      proposalPlantProductId: string;
    };
    refetchQueries?: string[];
    awaitRefetchQueries?: boolean;
  }) => Promise<unknown>;
  productId: ProposalProduct["id"];
};

function PlantProductSelectionFields({
  proposalPlantProduct,
  index,
  plantOptions,
  plantProductOptions,
  plantsLoading,
  search,
  pagination,
  inViewMode,
  control,
  updateProposalPlantProduct,
  productId,
}: PlantProductSelectionFieldsProps) {
  const [selectedPlantId, setSelectedPlantId] = useState<string>(
    proposalPlantProduct.plant?.plantId ?? ""
  );
  const { setValue } = useFormContext<FieldValues>();
  const [cachedPlantProductOptions, setCachedPlantProductOptions] = useState<
    Record<string, SelectOptions>
  >({});

  const getSelectedPlantProductOptions = useCallback(
    (fieldValue: SelectOptions[number]["value"]) => {
      const liveOptions = plantProductOptions[selectedPlantId] || [];

      if (liveOptions.length > 0) {
        return liveOptions;
      }

      const cachedOptions = cachedPlantProductOptions[selectedPlantId] || [];
      if (cachedOptions.length > 0) {
        return cachedOptions;
      }

      if (
        fieldValue &&
        fieldValue === proposalPlantProduct.plantProductId &&
        proposalPlantProduct.type
      ) {
        return [
          {
            label: mapPlantProductTypeOptions(proposalPlantProduct.type),
            value: fieldValue,
          },
        ];
      }

      return [];
    },
    [
      cachedPlantProductOptions,
      plantProductOptions,
      proposalPlantProduct.plantProductId,
      proposalPlantProduct.type,
      selectedPlantId,
    ]
  );

  useEffect(() => {
    const options = plantProductOptions[selectedPlantId];
    if (!selectedPlantId || !options || options.length === 0) {
      return;
    }

    setCachedPlantProductOptions((prev) => {
      const previous = prev[selectedPlantId];
      if (previous && previous.length === options.length) {
        return prev;
      }

      return {
        ...prev,
        [selectedPlantId]: options,
      };
    });
  }, [plantProductOptions, selectedPlantId]);

  const handlePlantProductChange = useCallback(
    async (value: string | number) => {
      if (!value) return;

      const stringValue = String(value);
      const fieldName = `plantProducts.${index}.plantProductId`;

      setValue(fieldName, stringValue, {
        shouldDirty: false,
        shouldTouch: true,
      });

      await updateProposalPlantProduct({
        variables: {
          plantProductId: stringValue,
          proposalProductId: productId,
          proposalPlantProductId: proposalPlantProduct.id,
        },
        refetchQueries: ["customGetProposal"],
        awaitRefetchQueries: true,
      });

      setValue(fieldName, stringValue, {
        shouldDirty: false,
        shouldTouch: true,
      });
    },
    [index, setValue, updateProposalPlantProduct, productId, proposalPlantProduct.id]
  );

  if (inViewMode) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <MDBox display="flex" gap={2} mb={2}>
        <MDBox flex={1}>
          <ScrollAsyncAutocomplete
            options={plantOptions}
            value={{
              label:
                plantOptions.find((x) => x.value === selectedPlantId)?.label ??
                proposalPlantProduct.plant?.name ??
                "",
              value: selectedPlantId,
            }}
            onChange={(value) => {
              const newPlantId = value ? String(value) : "";
              if (newPlantId !== selectedPlantId) {
                setSelectedPlantId(newPlantId);
                setValue(`plantProducts.${index}.plantProductId`, "", {
                  shouldDirty: false,
                  shouldTouch: true,
                });
              }
            }}
            loading={plantsLoading}
            label="Plant"
            search={search}
            onLoadMore={pagination.loadMore}
            hasMore={pagination.hasNextPage}
            variant="standard"
          />
        </MDBox>
        <MDBox flex={1}>
          <Controller
            key={`${proposalPlantProduct.id}-${proposalPlantProduct.plantProductId}`}
            name={`plantProducts.${index}.plantProductId`}
            control={control}
            defaultValue={proposalPlantProduct.plantProductId}
            render={({ field }) => (
              <ScrollAsyncAutocomplete
                options={getSelectedPlantProductOptions(field.value)}
                value={{
                  label:
                    getSelectedPlantProductOptions(field.value).find((x) => x.value === field.value)
                      ?.label ?? "",
                  value: field.value ?? "",
                }}
                onChange={async (value) => {
                  await handlePlantProductChange(value);
                }}
                loading={plantsLoading}
                label="Plant Product"
                search={() => {}}
                onLoadMore={() => {}}
                hasMore={false}
                variant="standard"
              />
            )}
          />
        </MDBox>
      </MDBox>
    </Grid>
  );
}

function MaterialCosts({
  onAddMaterialModalOpen,
  onAddMaterialNoPlantModalOpen,
}: {
  onAddMaterialModalOpen: () => void;
  onAddMaterialNoPlantModalOpen: () => void;
}) {
  const { inViewMode } = useContext(ProposalContext);
  const {
    control,
    register,
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitSuccessful },
  } = useFormContext<FieldValues>();
  const { product, defaultValues, onSubmit } = useContext(StageProductContext);
  const [updateProposalPlantProduct] = useUpdateProposalPlantProduct();
  const [deleteProposalPlantProduct, { loading: deleteLoading }] = useDeleteProposalPlantProduct();

  const { requiresPlant, requiresTrucking } = useProposalProductDetails(product);

  const [plantProductToDelete, setPlantProductToDelete] = useState<string | null>(null);
  const [expandedPlantTruckingTables, setExpandedPlantTruckingTables] = useState<
    Record<string, boolean>
  >({});
  // Track "Use Weight" toggle locally so the UI updates immediately even before
  // RHF values are reflected in the parent submission payload.
  const [transientRateOnWeightByPlantId, setTransientRateOnWeightByPlantId] = useState<
    Record<string, boolean>
  >({});

  const acceptFn = useCallback(async (): Promise<boolean> => {
    if (!plantProductToDelete) return false;

    try {
      await deleteProposalPlantProduct({
        variables: { id: plantProductToDelete },
      });
      return true;
    } catch (error) {
      return false;
    }
  }, [deleteProposalPlantProduct, plantProductToDelete]);

  const cancelFn = useCallback(async () => {
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const handleDeletePlantProduct = useCallback(
    (plantProductId: string) => {
      setPlantProductToDelete(plantProductId);
      setOpen(true);
    },
    [setOpen]
  );

  const proposalPlantProducts = (product?.proposalPlantProducts || []).filter(
    (proposalPlantProduct): proposalPlantProduct is ProposalPlantProduct =>
      Boolean(proposalPlantProduct)
  );
  const proposalMaterialNoPlants = product?.proposalMaterialNoPlants || [];

  return (
    <Grid spacing={1} container py={3} pt={0}>
      {!inViewMode && (
        <Grid item xs={12}>
          <MDBox mb={2} align="right">
            <MDButton
              size="small"
              color="info"
              variant="outlined"
              onClick={requiresPlant ? onAddMaterialModalOpen : onAddMaterialNoPlantModalOpen}
            >
              Add Material
            </MDButton>
          </MDBox>
        </Grid>
      )}

      {requiresPlant &&
        proposalPlantProducts.length > 0 &&
        proposalPlantProducts.map((proposalPlantProduct, index) => {
          const rateOnWeight =
            transientRateOnWeightByPlantId[proposalPlantProduct.id] ??
            watch(`plantProducts.${index}.rateOnWeight`) ??
            proposalPlantProduct.rateOnWeight ??
            false;
          return (
            <Grid item xs={12} key={proposalPlantProduct.id}>
              <PlantOptions selectedPlant={proposalPlantProduct.plant}>
                {({
                  plantOptions,
                  plantProductOptions,
                  loading: plantsLoading,
                  search,
                  pagination,
                }) => (
                  <Accordion elevation={3} defaultExpanded>
                    <AccordionSummary
                      sx={{ backgroundColor: "rgba(100, 100, 150, 0.1)" }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`plant-product-${index}-content`}
                      id={`plant-product-${index}-header`}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center"
                      >
                        <MDTypography variant="h6">
                          {proposalPlantProduct.plant?.name || `Plant ${index + 1}`}
                          {proposalPlantProduct.type && (
                            <MDTypography
                              component="span"
                              variant="body2"
                              sx={{ ml: 1, fontWeight: "normal", color: "text.secondary" }}
                            >
                              - {mapPlantProductTypeOptions(proposalPlantProduct.type)}
                            </MDTypography>
                          )}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <MDTypography
                            variant="body2"
                            sx={{ fontSize: "0.9em", fontWeight: "bold" }}
                          >
                            {formatCentsToUSD(
                              proposalPlantProduct.costMaterial +
                                proposalPlantProduct.costTransferTruck || 0
                            )}
                          </MDTypography>
                          {!inViewMode && (
                            <MDButton
                              size="small"
                              color="error"
                              variant="text"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlantProduct(proposalPlantProduct.id);
                              }}
                              disabled={deleteLoading}
                              sx={{ minWidth: "auto", padding: "4px" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </MDButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid spacing={1} container>
                        <PlantProductSelectionFields
                          proposalPlantProduct={proposalPlantProduct}
                          index={index}
                          plantOptions={plantOptions}
                          plantProductOptions={plantProductOptions}
                          plantsLoading={plantsLoading}
                          search={search}
                          pagination={pagination}
                          inViewMode={inViewMode}
                          control={control}
                          updateProposalPlantProduct={updateProposalPlantProduct}
                          productId={product.id}
                        />
                        <Grid
                          className="table-form-field-inline-content"
                          sx={{ "& .table-form-field-inline-items": { pb: 0 } }}
                        >
                          <Grid className="table-form-field-inline-items">
                            <FormField
                              disabled={inViewMode}
                              align="right"
                              label={`Material needed in ${
                                pluralMeasurementMaterial[product.material.measurementMaterial] ??
                                "Units"
                              }`}
                              placeholder={(proposalPlantProduct.materialNeeded ?? 0).toFixed(1)}
                              {...register(`plantProducts.${index}.materialNeededOverride`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Grid>
                          <Grid className="table-form-field-inline-items">
                            <Controller
                              control={control}
                              name={`plantProducts.${index}.rateOverride`}
                              render={({ field, fieldState: { error } }) => (
                                <FormField
                                  disabled={inViewMode}
                                  align="right"
                                  label="Material Cost"
                                  placeholder={proposalPlantProduct.rate / 100}
                                  error={error}
                                  {...currencyEndAdornment}
                                  {...field}
                                  inputProps={{
                                    type: "number",
                                    min: "0",
                                    step: "0.01",
                                    inputMode: "decimal",
                                  }}
                                  onChange={(event) =>
                                    !event.target.value
                                      ? field.onChange(null)
                                      : field.onChange(
                                          new Decimal(event.target.value)
                                            .toDecimalPlaces(2)
                                            .toNumber()
                                        )
                                  }
                                  value={field.value || field.value === 0 ? field.value : ""}
                                />
                              )}
                            />
                          </Grid>
                          <Grid className="table-form-field-inline-items">
                            <FormField
                              disabled={inViewMode}
                              align="right"
                              label="Application Rate"
                              placeholder={proposalPlantProduct.applicationRate}
                              {...register(`plantProducts.${index}.applicationRateOverride`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Grid>
                          <Grid className="table-form-field-inline-items">
                            <FormField
                              disabled={inViewMode}
                              label="Transfer Truck Round Trip Travel Time"
                              align="right"
                              placeholder={proposalPlantProduct.transferTruckRoundTripTimeHrs}
                              {...timeEndAdornment}
                              {...register(
                                `plantProducts.${index}.transferTruckRoundTripTimeHrsOverride`,
                                {
                                  valueAsNumber: true,
                                }
                              )}
                            />
                          </Grid>
                          <Grid className="table-form-field-inline-items">
                            <FormField
                              disabled={inViewMode}
                              align="right"
                              label="Truck Load Time Buffer"
                              placeholder={convertHrsToMins(
                                proposalPlantProduct.transferTruckLoadTimeBuffer
                              )}
                              {...minuteEndAdornment}
                              {...register(`plantProducts.${index}.transferTruckLoadTimeBuffer`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Grid>
                          <Grid className="table-form-field-inline-items">
                            <FormField
                              disabled={inViewMode}
                              align="right"
                              label="Truck Unload Time Buffer"
                              placeholder={convertHrsToMins(
                                proposalPlantProduct.transferTruckUnloadTimeBuffer
                              )}
                              {...minuteEndAdornment}
                              {...register(`plantProducts.${index}.transferTruckUnloadTimeBuffer`, {
                                valueAsNumber: true,
                              })}
                            />
                          </Grid>
                        </Grid>

                        <Grid
                          container
                          sx={{ minHeight: "40px", alignItems: "center", flexWrap: "nowrap" }}
                        >
                          <Grid item xs="auto" sx={{ ml: 0 }}>
                            <Switch
                              disabled={inViewMode}
                              checked={Boolean(rateOnWeight)}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setTransientRateOnWeightByPlantId((prev) => ({
                                  ...prev,
                                  [proposalPlantProduct.id]: checked,
                                }));
                                setValue(`plantProducts.${index}.rateOnWeight`, checked, {
                                  shouldDirty: false,
                                  shouldTouch: false,
                                  shouldValidate: false,
                                });
                              }}
                              sx={{ display: "inline-flex", m: 0 }}
                            />
                          </Grid>
                          <Grid item xs sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                            <MDTypography
                              variant="body2"
                              sx={{ fontSize: "0.8em", whiteSpace: "nowrap", pl: 1 }}
                            >
                              Use Weight to calculate trucking
                            </MDTypography>
                          </Grid>
                          <Grid item xs="auto" sx={{ ml: "auto", mr: 2 }}>
                            <MDButton
                              size="small"
                              variant="text"
                              color="info"
                              onClick={() =>
                                setExpandedPlantTruckingTables((prev) => {
                                  const tableKey = proposalPlantProduct.id || `plant-${index}`;
                                  return { ...prev, [tableKey]: !prev[tableKey] };
                                })
                              }
                            >
                              {!!expandedPlantTruckingTables[
                                proposalPlantProduct.id || `plant-${index}`
                              ]
                                ? "Hide Extra Rows"
                                : "Show All Rows"}
                            </MDButton>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <PlantProductTruckingTable
                            proposalPlantProduct={proposalPlantProduct}
                            productMaterialNeeded={product.material.materialNeeded}
                            rateOnWeight={rateOnWeight}
                            showAdvancedColumns={
                              !!expandedPlantTruckingTables[
                                proposalPlantProduct.id || `plant-${index}`
                              ]
                            }
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}
              </PlantOptions>
            </Grid>
          );
        })}

      {!requiresPlant && (
        <Grid item xs={12}>
          <MaterialsNoPlantSection
            proposalMaterialNoPlants={proposalMaterialNoPlants}
            inViewMode={inViewMode}
          />
        </Grid>
      )}

      {requiresTrucking && (
        <Grid item xs={12} sx={{ marginTop: 3, marginBottom: 0, paddingBottom: 0 }}>
          <MDBox display="flex" justifyContent="flex-end" gap={2}>
            <MDTypography variant="body2" sx={{ fontSize: "0.8em", fontWeight: "bold" }}>
              Total Material Cost:
            </MDTypography>
            <MDTypography variant="body2" sx={{ fontSize: "0.8em", fontWeight: "bold" }}>
              {formatCentsToUSD(+product.costMaterial)}
            </MDTypography>
          </MDBox>
        </Grid>
      )}

      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Delete Material"
        content="Are you sure you want to delete this material from the proposal product? This action cannot be undone."
        acceptText="Delete Material"
        cancelText="Cancel"
      />
    </Grid>
  );
}

export default MaterialCosts;
